import React, { useState, useContext } from 'react';
import { TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { AppContext } from '../../App';
import useGlynt from '../hooks/useGlynt';
import errorResponses from '../../errorResponses';
import { validateLabel } from '../../utils/validationUtils';

interface OrgDataPoolDialogProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  dataPoolNames: string[];
}

const OrgDataPoolDialog: React.FC<OrgDataPoolDialogProps> = ({
  open,
  onClose,
  orgId,
  dataPoolNames,
}) => {
  const { setToast } = useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();
  const [name, setName] = useState('');

  const validation = validateLabel(name, dataPoolNames);

  const createDataPool = useMutation({
    mutationFn: async (label: string) =>
      (await glynt).dataPool.create({
        organization: orgId,
        label,
        glynt_tags: ['core_version:4.0.0'],
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['datapools'] });
      setToast({ variant: 'success', open: true, message: 'Data Pool created successfully.' });
      setName('');
      onClose();
    },
    onError: () => {
      setToast({ variant: 'error', open: true, message: errorResponses.failedToCreateDataPool });
    },
  });

  const handleClose = () => {
    if (!createDataPool.isPending) {
      setName('');
      onClose();
    }
  };

  const handleSubmit = () => {
    createDataPool.mutate(name);
  };

  return (
    <DialogGlynt open={open} closeModal={handleClose} cypressId="dialog-new-data-pool" loading={createDataPool.isPending}>
      <DialogTitleGlynt title="New Data Pool" closeModal={handleClose} loading={createDataPool.isPending} />
      <DialogContentGlynt>
        <TextField
          autoComplete="off"
          data-cy="input-new-data-pool"
          label="Data Pool Name"
          variant="outlined"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={name.length > 0 && !validation.isValid}
          helperText={name.length > 0 && !validation.isValid ? validation.errorMessage : ''}
          autoFocus
        />
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={handleClose}
        cancelCypressId="btn-cancel-new-data-pool"
        submitCypressId="btn-create-data-pool"
        clearCypressId=""
        submitButtonText="Create"
        submitDisabled={!validation.isValid}
        submitLoading={createDataPool.isPending}
      />
    </DialogGlynt>
  );
};

export default OrgDataPoolDialog;
